import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { QUERY_CLIENT_PREFIX } from "./constants";

export class PersistedQueryManager {
  private static instance: PersistedQueryManager | null = null;
  private client: QueryClient;

  private constructor(client: QueryClient) {
    this.client = client;
  }

  static getInstance(client: QueryClient): PersistedQueryManager {
    if (!PersistedQueryManager.instance) {
      PersistedQueryManager.instance = new PersistedQueryManager(client);
    }
    return PersistedQueryManager.instance;
  }

  private getPersisterQueryKey(queryKey: unknown) {
    try {
      const key = JSON.stringify(queryKey);
      if (!key) {
        return undefined;
      }

      return `${QUERY_CLIENT_PREFIX}-${key}`;
    } catch (e) {
      console.error("Error getting persister query key", e);
      return undefined;
    }
  }

  removeQuery(args: Parameters<QueryClient["removeQueries"]>[0]) {
    const persisterQueryKey = this.getPersisterQueryKey(args?.queryKey);
    persisterQueryKey && window.sessionStorage.removeItem(persisterQueryKey);
    // notify subscribers that the query has been removed
    this.client.resetQueries(args);
    // remove the query from the cache
    this.client.removeQueries(args);
  }

  invalidateQueries(args: Parameters<QueryClient["invalidateQueries"]>[0]) {
    this.client.invalidateQueries(args);
    const persisterQueryKey = this.getPersisterQueryKey(args?.queryKey);
    persisterQueryKey && window.sessionStorage.removeItem(persisterQueryKey);
  }

  clearAll() {
    this.client.clear();
    for (let i = 0; i < window.sessionStorage.length; i++) {
      const key = window.sessionStorage.key(i);
      if (key?.startsWith(QUERY_CLIENT_PREFIX)) {
        window.sessionStorage.removeItem(key);
      }
    }
  }
}

export const usePersistedQueryManager = () => {
  const queryClient = useQueryClient();
  return PersistedQueryManager.getInstance(queryClient);
};
